// What code reaches: from a start (an endpoint's handler, a test and what runs with it), every
// member it calls, step by step through the calls of the code map, into interface and virtual
// members' implementations, through property getters, down to the EF Core entities it uses. Calls
// made to set up or check a fake (`CodeCall.mock`) are not followed: they do not run the member.
// Where a value of the code's types is handed to a package (`sender.Send(new PlaceOrder(...))`),
// the reach goes on, as a guess, to the code's handlers of that type (handlers.ts). A test's reach
// also goes into the endpoints its HTTP requests reach (requests.ts).
import type { CodeCall, CodeEndpoint, CodeHandledBy, CodeHandler, CodeHandoff, CodeMap, CodeMember, CodeRequest } from './model';

export interface ReachOptions {
  /** Follow calls guessed by name (default true). */
  guesses?: boolean;
  /** Also count fields, constants, events and auto-properties read or written (default false: only properties with code). */
  fields?: boolean;
  /** Stop after this many members (default 800). */
  limit?: number;
}

export interface Reach {
  /** Members reached, by how many steps from the start (the start's own members are 0). */
  depth: Map<string, number>;
  /** The calls followed: from a reached member, or from a minimal API endpoint itself (a lambda handler's calls, `from` the endpoint id). */
  calls: CodeCall[];
  /** Interface, abstract or virtual members, and the implementations the reach went on to. */
  dispatch: { from: string; to: string }[];
  /** Types created that declare no constructor. */
  types: string[];
  /** EF Core entities reached through their DbSets. */
  data: string[];
  /** Values of the code's types the reached code hands to the framework or packages: where it leaves the code. */
  handoffs: CodeHandoff[];
  /** Handlers of what is handed over that the reach went on to (a guess, followed with guessed calls). */
  handled: CodeHandledBy[];
  /** HTTP requests the reached code sends, each to an endpoint it went into (when the start follows requests). */
  requests: RequestHit[];
  /** Files with something reached, the start's own first when it has something reached. */
  files: string[];
  /** The limit was hit: there is more. */
  truncated: boolean;
}

/** An HTTP request that reaches an endpoint of the code. */
export interface RequestHit {
  /** The member sending it. */
  from: string;
  endpoint: string;
  /** Where the endpoint's code is: its handler, or the member mapping its handler lambda. */
  to: string | null;
  method: string;
  url: string;
  certainty: 'exact' | 'guess';
  file: string;
  line: number;
}

export interface ReachStart {
  /** The members to start from. */
  members: string[];
  /** A minimal API endpoint whose handler lambda's calls are the start's own. */
  endpoint?: string;
  /** The file to list first. */
  file?: string;
  /** Whether a call to `member` (interface, abstract or virtual) goes on to `impl`; default yes. */
  dispatchTo?: (member: CodeMember, impl: CodeMember) => boolean;
  /** Whether a hand-off goes on to `handler`; default yes. */
  handledBy?: (handler: CodeMember) => boolean;
  /** Go into the endpoints the code's HTTP requests reach (a test's reach); default no. */
  requests?: boolean;
}

export interface ReachIndex {
  members: Map<string, CodeMember>;
  callsFrom: Map<string, CodeCall[]>;
  callsOfEndpoint: Map<string, CodeCall[]>;
  dataFrom: Map<string, string[]>;
  dataOfEndpoint: Map<string, string[]>;
  handoffsFrom: Map<string, CodeHandoff[]>;
  handoffsOfEndpoint: Map<string, CodeHandoff[]>;
  requestsFrom: Map<string, CodeRequest[]>;
  requestsOfEndpoint: Map<string, CodeRequest[]>;
  endpoints: Map<string, CodeEndpoint>;
  /** The code's handlers of each type. */
  handlersOf: Map<string, CodeHandler[]>;
  /** Types that derive from or implement each type, directly. */
  subtypes: Map<string, string[]>;
  /** Files of test code (`CodeFile.test`). */
  testFiles: Set<string>;
  implementations: (member: CodeMember) => CodeMember[];
}

const indexes = new WeakMap<CodeMap, ReachIndex>();

function push<T>(map: Map<string, T[]>, key: string, value: T): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

export function reachIndex(map: CodeMap): ReachIndex {
  const cached = indexes.get(map);
  if (cached) return cached;
  const members = new Map(map.members.map((m) => [m.id, m]));
  const callsFrom = new Map<string, CodeCall[]>();
  const callsOfEndpoint = new Map<string, CodeCall[]>();
  for (const c of map.calls) {
    if (c.endpoint) push(callsOfEndpoint, c.endpoint, c);
    else push(callsFrom, c.from, c);
  }
  const dataFrom = new Map<string, string[]>();
  const dataOfEndpoint = new Map<string, string[]>();
  for (const u of map.uses) {
    if (u.kind !== 'data') continue;
    if (u.endpoint) push(dataOfEndpoint, u.endpoint, u.to);
    else push(dataFrom, u.from, u.to);
  }
  const handoffsFrom = new Map<string, CodeHandoff[]>();
  const handoffsOfEndpoint = new Map<string, CodeHandoff[]>();
  for (const h of map.handoffs) {
    if (h.endpoint) push(handoffsOfEndpoint, h.endpoint, h);
    else push(handoffsFrom, h.from, h);
  }
  const requestsFrom = new Map<string, CodeRequest[]>();
  const requestsOfEndpoint = new Map<string, CodeRequest[]>();
  for (const r of map.requests) {
    if (r.endpoint) push(requestsOfEndpoint, r.endpoint, r);
    else push(requestsFrom, r.from, r);
  }
  const endpoints = new Map(map.endpoints.map((e) => [e.id, e]));
  const handlersOf = new Map<string, CodeHandler[]>();
  for (const h of map.handlers) push(handlersOf, h.type, h);
  const subtypes = new Map<string, string[]>();
  for (const t of map.types) for (const sup of [...(t.base ? [t.base] : []), ...t.interfaces]) push(subtypes, sup, t.id);
  const membersOf = new Map<string, CodeMember[]>();
  for (const m of map.members) push(membersOf, m.type, m);
  const types = new Map(map.types.map((t) => [t.id, t]));
  const cache = new Map<string, CodeMember[]>();
  const implementations = (member: CodeMember): CodeMember[] => {
    const known = cache.get(member.id);
    if (known) return known;
    const out: CodeMember[] = [];
    const owner = types.get(member.type);
    const dispatches = owner?.kind === 'interface' || member.modifiers.some((m) => m === 'abstract' || m === 'virtual' || m === 'override');
    if (dispatches) {
      // Through a generic type the signature changes (`AddAsync(T, ...)` is `AddAsync(Order, ...)` in a
      // class implementing IRepository<Order>): the same name and number of parameters will do.
      const generic = (owner?.typeParams.length ?? 0) > 0;
      const arity = paramCount(member.signature);
      const seen = new Set<string>();
      const queue = [...(subtypes.get(member.type) ?? [])];
      while (queue.length) {
        const t = queue.shift()!;
        if (seen.has(t)) continue;
        seen.add(t);
        const candidates = (membersOf.get(t) ?? []).filter((m) => m.kind === member.kind && m.body);
        const exact = candidates.filter((m) => m.signature === member.signature);
        out.push(...(exact.length || !generic ? exact : candidates.filter((m) => m.name === member.name && paramCount(m.signature) === arity)));
        queue.push(...(subtypes.get(t) ?? []));
      }
    }
    cache.set(member.id, out);
    return out;
  };
  const testFiles = new Set(map.files.filter((f) => f.test).map((f) => f.path));
  const index = { members, callsFrom, callsOfEndpoint, dataFrom, dataOfEndpoint, handoffsFrom, handoffsOfEndpoint, requestsFrom, requestsOfEndpoint, endpoints, handlersOf, subtypes, testFiles, implementations };
  indexes.set(map, index);
  return index;
}

/** How many parameters a signature lists: 2 for `AddAsync(T, CancellationToken)`, -1 for a property. */
function paramCount(signature: string): number {
  const open = signature.indexOf('(');
  if (open < 0) return -1;
  const inner = signature.slice(open + 1, signature.lastIndexOf(')')).trim();
  if (!inner) return 0;
  let depth = 0;
  let count = 1;
  for (const ch of inner) {
    if (ch === '<' || ch === '(' || ch === '[') depth++;
    else if (ch === '>' || ch === ')' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) count++;
  }
  return count;
}

/** Everything reached from a start, step by step. */
export function reach(map: CodeMap, start: ReachStart, options: ReachOptions = {}): Reach {
  const { guesses = true, fields = false, limit = 800 } = options;
  const index = reachIndex(map);
  const depth = new Map<string, number>();
  const calls: CodeCall[] = [];
  const dispatch: { from: string; to: string }[] = [];
  const types = new Set<string>();
  const data = new Set<string>();
  const handoffs: CodeHandoff[] = [];
  const handled: CodeHandledBy[] = [];
  const requests: RequestHit[] = [];
  /** Endpoints gone into through HTTP requests (their handler lambdas' tags). */
  const entered = new Set<string>();
  const queue: string[] = [];
  let truncated = false;

  const visit = (id: string, d: number): boolean => {
    if (depth.has(id)) return true;
    if (depth.size >= limit) {
      truncated = true;
      return false;
    }
    depth.set(id, d);
    queue.push(id);
    return true;
  };
  const follow = (c: CodeCall, d: number, from: string) => {
    if (c.mock || (c.certainty === 'guess' && !guesses)) return;
    if (c.endpoint && c.endpoint !== start.endpoint && !entered.has(c.endpoint)) return;
    const target = index.members.get(c.to);
    if (!target) {
      // `new T()` for a type with no constructor of its own.
      if (c.kind === 'new') {
        types.add(c.to);
        calls.push(from === c.from ? c : { ...c, from });
      }
      return;
    }
    // Reading a property runs its getter: follow it when it has code of its own (not an auto-property).
    if (c.kind === 'access' && !fields && !((target.kind === 'property' || target.kind === 'indexer') && target.body)) return;
    if (visit(target.id, d + 1)) calls.push(from === c.from ? c : { ...c, from });
  };

  // A value handed to a package: its handlers in the code probably run.
  const handOff = (h: CodeHandoff, d: number, from: string) => {
    handoffs.push(from === h.from ? h : { ...h, from });
    if (!guesses) return;
    for (const type of h.types) {
      for (const handler of index.handlersOf.get(type) ?? []) {
        const target = index.members.get(handler.member);
        if (!target || (start.handledBy && !start.handledBy(target))) continue;
        if (visit(target.id, d + 1)) handled.push({ from, to: target.id, type, via: h.to, file: h.file, line: h.line });
      }
    }
  };

  // An HTTP request reaching an endpoint of the code: on into its handler, or its handler lambda's code.
  const request = (r: CodeRequest, d: number, from: string) => {
    if (!start.requests || (r.certainty === 'guess' && !guesses)) return;
    for (const id of r.endpoints) {
      const e = index.endpoints.get(id);
      if (!e) continue;
      const to = e.handler ?? e.mappedIn ?? null;
      requests.push({ from, endpoint: id, to, method: r.method, url: r.url, certainty: r.certainty, file: r.file, line: r.line });
      if (e.handler) visit(e.handler, d + 1);
      const tag = e.sameHandler ?? e.id;
      if (e.handler || entered.has(tag)) continue;
      entered.add(tag);
      // The lambda lives in the member mapping it: shown, but its own code maps routes at start-up and is not followed.
      if (e.mappedIn && !depth.has(e.mappedIn) && depth.size < limit) depth.set(e.mappedIn, d + 1);
      const around = e.mappedIn ?? tag;
      for (const c of index.callsOfEndpoint.get(tag) ?? []) follow(c, d + 1, around);
      for (const x of index.dataOfEndpoint.get(tag) ?? []) data.add(x);
      for (const h of index.handoffsOfEndpoint.get(tag) ?? []) handOff(h, d + 1, around);
      for (const q of index.requestsOfEndpoint.get(tag) ?? []) request(q, d + 1, around);
    }
  };

  for (const id of start.members) visit(id, 0);
  if (start.endpoint) {
    for (const c of index.callsOfEndpoint.get(start.endpoint) ?? []) follow(c, 0, start.endpoint);
    for (const e of index.dataOfEndpoint.get(start.endpoint) ?? []) data.add(e);
    for (const h of index.handoffsOfEndpoint.get(start.endpoint) ?? []) handOff(h, 0, start.endpoint);
  }

  while (queue.length) {
    const id = queue.shift()!;
    const d = depth.get(id)!;
    const member = index.members.get(id);
    if (!member) continue;
    for (const c of index.callsFrom.get(id) ?? []) follow(c, d, id);
    for (const e of index.dataFrom.get(id) ?? []) data.add(e);
    for (const h of index.handoffsFrom.get(id) ?? []) handOff(h, d, id);
    for (const r of index.requestsFrom.get(id) ?? []) request(r, d, id);
    for (const impl of index.implementations(member)) {
      if (start.dispatchTo && !start.dispatchTo(member, impl)) continue;
      if (visit(impl.id, d + 1)) dispatch.push({ from: id, to: impl.id });
    }
  }

  const fileOf = (id: string) => index.members.get(id)?.file ?? map.types.find((t) => t.id === id)?.parts[0]?.file;
  // Files with something reached; the start's own file first when it is one of them.
  const reachedFiles = new Set([...depth.keys(), ...types].map((id) => fileOf(id)!).filter(Boolean));
  const first = start.file;
  const files = first && reachedFiles.has(first) ? [first, ...[...reachedFiles].filter((f) => f !== first)] : [...reachedFiles];
  return { depth, calls, dispatch, types: [...types], data: [...data], handoffs, handled, requests, files, truncated };
}

const handledCache = new WeakMap<CodeMap, CodeHandledBy[]>();

/** Every hand-off of the code with a handler of what it hands over, as the code map draws them (dashed). */
export function handledBy(map: CodeMap): CodeHandledBy[] {
  const cached = handledCache.get(map);
  if (cached) return cached;
  const { handlersOf } = reachIndex(map);
  const out: CodeHandledBy[] = [];
  for (const h of map.handoffs) {
    for (const type of h.types) {
      for (const handler of handlersOf.get(type) ?? []) out.push({ from: h.endpoint ?? h.from, to: handler.member, type, via: h.to, file: h.file, line: h.line });
    }
  }
  handledCache.set(map, out);
  return out;
}
